import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { FileText, Clock, CreditCard, Mail, Phone, AlertCircle } from 'lucide-react';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';

const Logo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="p-1 rounded-lg bg-blue-600">
              <Logo className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-blue-600">Quick Hire</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="rounded-full px-6"
          >
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-12 text-center">
        <Badge className="mb-4" variant="secondary">Legal Information</Badge>
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          Refund Policy
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          At Quick Hire, we're committed to your satisfaction. This Refund Policy outlines the terms and conditions for requesting refunds on our resume building services.
        </p>
      </section>

      {/* Policy Overview Cards */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">Policy Overview</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-7 w-7 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">14-Day Window</h3>
            <p className="text-gray-600">
              Request a refund within 14 days of your purchase
            </p>
          </Card>

          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-7 w-7 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Original Payment Method</h3>
            <p className="text-gray-600">
              Refunds processed to your original payment source
            </p>
          </Card>

          <Card className="p-6 text-center hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-7 w-7 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">5-7 Business Days</h3>
            <p className="text-gray-600">
              Average processing time for refunds
            </p>
          </Card>
        </div>
      </section>

      {/* Detailed Policy */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <Card className="p-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Our Refund Terms</h2>

          <div className="space-y-8">
            {/* Refund Eligibility */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">1. Refund Eligibility</h3>
              <p className="text-gray-700 mb-3">
                You are eligible for a full refund if you meet the following conditions:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>You request a refund within <strong>14 days</strong> of your purchase date</li>
                <li>The resume PDF was not downloaded successfully due to technical issues on our platform</li>
                <li>You experienced a payment error resulting in duplicate charges</li>
                <li>The service did not function as described or advertised</li>
              </ul>
            </div>

            {/* Non-Refundable Situations */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">2. Non-Refundable Situations</h3>
              <p className="text-gray-700 mb-3">
                Refunds will <strong>not</strong> be issued in the following cases:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>After 14 days from the date of purchase</li>
                <li>If you successfully downloaded your resume PDF</li>
                <li>Change of mind after receiving the completed product</li>
                <li>Violation of our Terms of Service or acceptable use policies</li>
                <li>If you provided incorrect personal information in your resume</li>
              </ul>
            </div>

            {/* How to Request */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">3. How to Request a Refund</h3>
              <p className="text-gray-700 mb-3">
                To request a refund, please follow these steps:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                <li>Email our support team at <a href="mailto:support@quick-hire.org" className="text-blue-600 hover:underline font-semibold">support@quick-hire.org</a></li>
                <li>Include your order number or transaction ID</li>
                <li>Provide the email address associated with your account</li>
                <li>Clearly state the reason for your refund request</li>
                <li>Allow up to 48 hours for our team to review your request</li>
              </ol>
            </div>

            {/* Processing Time */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">4. Refund Processing Time</h3>
              <p className="text-gray-700">
                Once your refund request is approved, please allow <strong>5-7 business days</strong> for the refund to appear in your account. The exact timing may depend on your payment provider or financial institution. Refunds are issued to the original payment method used during purchase.
              </p>
            </div>

            {/* Partial Refunds */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">5. Partial Refunds</h3>
              <p className="text-gray-700">
                In certain circumstances, we may offer partial refunds at our discretion. This may apply to situations where you have used part of our service but encountered issues preventing full satisfaction.
              </p>
            </div>

            {/* Customer Support */}
            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">6. Customer Support</h3>
              <p className="text-gray-700 mb-3">
                Before requesting a refund, we encourage you to contact our support team. We're here to help resolve any issues you may encounter with our service.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* FAQ Section */}
      <section className="bg-gradient-to-tr from-indigo-50 via-blue-100 to-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Refund <Badge className="ml-2" variant="default">FAQs</Badge>
          </h2>
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1">
              <AccordionTrigger>
                What if I downloaded my resume by mistake?
              </AccordionTrigger>
              <AccordionContent>
                Once a resume PDF has been successfully downloaded, we cannot issue a refund as the digital product has been delivered. We recommend previewing your resume carefully before making a payment.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>
                How long does it take to get my refund?
              </AccordionTrigger>
              <AccordionContent>
                Approved refunds are processed within 5-7 business days. The timing may vary depending on your bank or payment provider.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>
                Can I get a refund if I don't like the template?
              </AccordionTrigger>
              <AccordionContent>
                All our templates are available for preview before purchase. We encourage you to review all available templates in your dashboard before making payment. Refunds are not issued for template preferences.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>
                What if I was charged twice by mistake?
              </AccordionTrigger>
              <AccordionContent>
                If you were charged multiple times for the same transaction due to a technical error, please contact us immediately with your transaction details. We will investigate and issue a refund for the duplicate charge.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>
                Do you offer refunds for technical issues?
              </AccordionTrigger>
              <AccordionContent>
                Yes. If you experience technical issues that prevent you from downloading your resume after payment, please contact our support team immediately. We will either resolve the issue or process a full refund.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <Card className="p-8 bg-blue-600 text-white">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Need Help?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Our support team is available 24/7 to assist you with any refund inquiries.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6">
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5" />
                <a href="mailto:support@quick-hire.org" className="hover:underline">
                  support@quick-hire.org
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-5 w-5" />
                <a href="tel:+353832007206" className="hover:underline">
                  +353832007206
                </a>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = 'mailto:support@quick-hire.org'}
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-6 text-lg"
            >
              Contact Support
            </Button>
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
